<template>
  <div class="input-wrapper" :class="wrapperClasses">
    <!-- <LockIcon 
      v-if="showLockIcon && readonly" 
      class="input-prefix-icon"
      size="small"
      :title="lockIconTitle"
    /> -->
    <input
      ref="inputRef"
      v-bind="$attrs"
      :class="inputClass"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :min="min"
      :max="max"
      :step="step"
      :aria-invalid="hasError"
      :aria-describedby="errorMessages.length > 0 ? `${inputId}-errors` : undefined"
      @input="onInput"
      @change="onChange"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    />
    <div v-if="$slots.suffix || clearable || showValidationIcon" class="input-suffix">
      <Icon
        v-if="showValidationIcon && !clearable"
        :name="hasError ? 'error' : 'check'"
        :size="14"
        :class="hasError ? 'validation-icon--error' : 'validation-icon--success'"
      />
      <Button
        v-if="clearable && modelValue"
        variant="icon"
        icon="close"
        size="small"
        @click="clear"
      />
      <slot name="suffix" />
    </div>
  </div>
  
  <div 
    v-if="errorMessages.length > 0" 
    :id="`${inputId}-errors`"
    class="error-messages"
    role="alert"
    aria-live="polite"
  >
    <div 
      v-for="(error, index) in errorMessages" 
      :key="index" 
      class="error-message"
    >
      <Icon name="error" :size="12" class="error-message-icon" />
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from './Button.vue';
import Icon from './Icon.vue';
// import LockIcon from './LockIcon.vue';

// Disable automatic attribute inheritance since we manually handle it
defineOptions({
  inheritAttrs: false
});

export interface InputBoxProps {
  modelValue: string | number;
  type?: 'text' | 'number' | 'password' | 'email' | 'url';
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  clearable?: boolean;
  min?: number;
  max?: number;
  step?: number;
  size?: 'small' | 'medium' | 'large';
  // Validation props
  errorMessages?: string[];
  hasError?: boolean;
  showValidationIcon?: boolean;
  validateOnBlur?: boolean;
  validateOnInput?: boolean;
  inputId?: string;
  // Permission props
  showLockIcon?: boolean;
  lockIconTitle?: string;
}

const props = withDefaults(defineProps<InputBoxProps>(), {
  type: 'text',
  disabled: false,
  readonly: false,
  clearable: false,
  size: 'medium',
  errorMessages: () => [],
  hasError: false,
  showValidationIcon: false,
  validateOnBlur: true,
  validateOnInput: false,
  inputId: () => `input-${Math.random().toString(36).substr(2, 9)}`,
  showLockIcon: false,
  lockIconTitle: 'Read-only due to permissions'
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  change: [value: string | number];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
  keydown: [event: KeyboardEvent];
  validate: [value: string | number];
}>();

const inputRef = ref<HTMLInputElement>();

const inputClass = computed(() => [
  'input',
  `input--${props.size}`,
  {
    'input--disabled': props.disabled,
    'input--readonly': props.readonly,
    'input--error': props.hasError && props.errorMessages.length > 0,
    'input--success': props.showValidationIcon && !props.hasError && props.modelValue
  }
]);

const wrapperClasses = computed(() => ({
  'input-wrapper--error': props.hasError && props.errorMessages.length > 0,
  'input-wrapper--success': props.showValidationIcon && !props.hasError && props.modelValue,
  'input-wrapper--with-validation': props.showValidationIcon
}));

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value: string | number = target.value;
  
  if (props.type === 'number') {
    value = target.valueAsNumber || 0;
  }
  
  emit('update:modelValue', value);
  
  if (props.validateOnInput) {
    emit('validate', value);
  }
};

const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value: string | number = target.value;
  
  if (props.type === 'number') {
    value = target.valueAsNumber || 0;
  }
  
  emit('change', value);
};

const onFocus = (event: FocusEvent) => {
  emit('focus', event);
};

const onBlur = (event: FocusEvent) => {
  emit('blur', event);
  
  if (props.validateOnBlur) {
    const value = props.type === 'number' ? Number(props.modelValue) : props.modelValue;
    emit('validate', value || '');
  }
};

const onKeydown = (event: KeyboardEvent) => {
  emit('keydown', event);
};

const clear = () => {
  emit('update:modelValue', '');
  emit('change', '');
  inputRef.value?.focus();
};

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  select: () => inputRef.value?.select(),
});
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
}

.input {
  @include input-style;
  flex: 1;
  width: 100%;
  outline: none;
  transition: border-color 0.1s ease;
  
  &--small {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 12px;
  }
  
  &--medium {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: var(--vscode-font-size);
  }
  
  &--large {
    padding: var(--spacing-md) var(--spacing-lg);
    font-size: 14px;
  }
  
  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &--readonly {
    background-color: var(--vscode-backgroundSecondary);
    cursor: default;
  }
  
  &--error {
    border-color: var(--vscode-inputValidation-errorBorder);
    background-color: var(--vscode-inputValidation-errorBackground);
    
    &:focus {
      border-color: var(--vscode-inputValidation-errorBorder);
    }
  }
  
  &--success {
    border-color: var(--vscode-terminal-ansiGreen);
    
    &:focus {
      border-color: var(--vscode-terminal-ansiGreen);
    }
  }
  
  &:focus {
    @include focus-outline;
    border-color: var(--vscode-focusBorder);
  }
  
  &::placeholder {
    color: var(--vscode-foreground);
    opacity: 0.6;
  }
}

.input-prefix-icon {
  position: absolute;
  left: var(--spacing-sm);
  pointer-events: none;
  z-index: 1;
}

.input-suffix {
  position: absolute;
  right: var(--spacing-xs);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

// Adjust input padding when lock icon is present
.input-wrapper:has(.input-prefix-icon) .input {
  padding-left: calc(var(--spacing-md) + 14px + var(--spacing-xs));
}

// Adjust input padding when suffix is present
.input-wrapper:has(.input-suffix) .input {
  padding-right: calc(var(--spacing-xxl) * 2);
}

// Validation styles
.validation-icon--error {
  color: var(--vscode-inputValidation-errorForeground);
}

.validation-icon--success {
  color: var(--vscode-terminal-ansiGreen);
}

.error-messages {
  margin-top: var(--spacing-xs);
}

.error-message {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  font-size: 12px;
  color: var(--vscode-inputValidation-errorForeground);
  background-color: var(--vscode-inputValidation-errorBackground);
  border: 1px solid var(--vscode-inputValidation-errorBorder);
  border-radius: var(--border-radius);
  padding: var(--spacing-xs) var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
  
  &:last-child {
    margin-bottom: 0;
  }
}

.error-message-icon {
  color: var(--vscode-inputValidation-errorForeground);
  flex-shrink: 0;
  margin-top: 1px; // Align with text baseline
}
</style>
